import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { db } from '../../utils/firebase';
import {
  loadProjectNotificationPref,
  saveProjectNotificationPref,
  PROJECT_NOTIFICATION_MODE_OPTIONS,
  type ProjectNotificationMode,
} from '../../utils/projectNotificationPrefs';
import './ProjectNotificationPrefsControl.css';

interface Props {
  projectId: string;
  projectName?: string | null;
  compact?: boolean;
}

const ProjectNotificationPrefsControl: React.FC<Props> = ({
  projectId,
  projectName,
  compact = false,
}) => {
  const { user } = useAuth();
  const [mode, setMode] = useState<ProjectNotificationMode>('all');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const next = await loadProjectNotificationPref(db, user.id, projectId);
        if (!cancelled) setMode(next);
      } catch (err) {
        console.error('[ProjectNotificationPrefs] load failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, projectId]);

  if (!user?.id || !projectId) return null;

  const apply = async (next: ProjectNotificationMode) => {
    setSaving(true);
    try {
      await saveProjectNotificationPref({
        db,
        userId: user.id,
        projectId,
        mode: next,
        projectName,
      });
      setMode(next);
      setOpen(false);
    } catch (err) {
      console.error('[ProjectNotificationPrefs] save failed', err);
      window.alert('Could not save notification preference.');
    } finally {
      setSaving(false);
    }
  };

  const label =
    PROJECT_NOTIFICATION_MODE_OPTIONS.find((o) => o.id === mode)?.label || 'All';

  return (
    <div className={`proj-notif-prefs${compact ? ' is-compact' : ''}`}>
      <button
        type="button"
        className="proj-notif-prefs-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Notifications: ${label}`}
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
      >
        <Bell size={14} aria-hidden />
        {!compact ? <span>Notifications: {label}</span> : null}
      </button>
      {open ? (
        <div className="proj-notif-prefs-menu" role="menu">
          {PROJECT_NOTIFICATION_MODE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="menuitemradio"
              aria-checked={mode === opt.id}
              className={`proj-notif-prefs-option${mode === opt.id ? ' is-active' : ''}`}
              onClick={() => void apply(opt.id)}
              disabled={saving}
            >
              <strong>{opt.label}</strong>
              <span>{opt.hint}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ProjectNotificationPrefsControl;
